import { SubscriptionAccess, SubscriptionStatus } from '../types/Subscription';

/**
 * The checkout return: Stripe sends the student back to
 * `STRIPE_SUCCESS_URL` (`/?checkout-success=true`), and the Google Ads
 * conversion for that purchase is reported once the subscription itself has
 * landed. "Pending" is remembered in sessionStorage so a reload between the
 * return and the subscription refresh does not lose the conversion.
 */

const PENDING_SUBSCRIPTION_CONVERSION_STORAGE_KEY =
  'ora-pending-google-ads-subscription-conversion';

/** The checkout return animation is still owed (it waits for a quiet page). */
const PENDING_SUBSCRIPTION_SUCCESS_STORAGE_KEY =
  'ora-pending-subscription-success';

export const CHECKOUT_SUCCESS_QUERY_PARAM = 'checkout-success';
export const MOBILE_CHECKOUT_QUERY_PARAM = 'mobile-checkout';

export const markPendingSubscriptionConversion = () => {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(PENDING_SUBSCRIPTION_CONVERSION_STORAGE_KEY, '1');
};

export const hasPendingSubscriptionConversion = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    sessionStorage.getItem(PENDING_SUBSCRIPTION_CONVERSION_STORAGE_KEY) === '1'
  );
};

export const clearPendingSubscriptionConversion = () => {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.removeItem(PENDING_SUBSCRIPTION_CONVERSION_STORAGE_KEY);
};

export const markPendingSubscriptionSuccess = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(PENDING_SUBSCRIPTION_SUCCESS_STORAGE_KEY, '1');
};

export const hasPendingSubscriptionSuccess = (): boolean =>
  typeof window !== 'undefined' &&
  sessionStorage.getItem(PENDING_SUBSCRIPTION_SUCCESS_STORAGE_KEY) === '1';

export const clearPendingSubscriptionSuccess = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PENDING_SUBSCRIPTION_SUCCESS_STORAGE_KEY);
};

/** The same location with the checkout params stripped, or itself. */
export const buildPathWithoutCheckoutParams = (location: {
  pathname: string;
  search: string;
  hash: string;
}): string => {
  const params = new URLSearchParams(location.search);
  params.delete(CHECKOUT_SUCCESS_QUERY_PARAM);
  params.delete(MOBILE_CHECKOUT_QUERY_PARAM);

  const query = params.toString();
  return `${location.pathname}${query ? `?${query}` : ''}${location.hash}`;
};

interface ConversionSubscription {
  accessLevel?: SubscriptionAccess;
  price?: number | null;
  status?: SubscriptionStatus | null;
  stripeSubscriptionId?: string | null;
  /** A one-time access purchase (the 5-year plan) has no Stripe subscription. */
  oneTimeAccessEndsAt?: string | null;
}

/**
 * A paid plan that has actually landed: a live Stripe subscription, or a
 * one-time access purchase (which the subscription row records as
 * `oneTimeAccessEndsAt`, often with its own row already canceled).
 */
export const isEligibleForSubscriptionConversion = (
  subscription: ConversionSubscription | null
) =>
  Boolean(
    subscription &&
    subscription.accessLevel !== SubscriptionAccess.FREE &&
    ((subscription.stripeSubscriptionId &&
      (subscription.status === SubscriptionStatus.ACTIVE ||
        subscription.status === SubscriptionStatus.TRIALING)) ||
      subscription.oneTimeAccessEndsAt)
  );

/** The id the conversion is reported (and de-duplicated) under. */
export const subscriptionConversionId = (
  subscription: ConversionSubscription | null
): string | null => {
  if (!subscription) return null;
  if (subscription.stripeSubscriptionId)
    return subscription.stripeSubscriptionId;
  if (subscription.oneTimeAccessEndsAt) {
    return `one-time:${subscription.oneTimeAccessEndsAt}`;
  }
  return null;
};
